import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Receipt, Loader2, Plus, Pencil, Trash2, X, Search, Filter, AlertTriangle, TrendingUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { AttachmentsPanel } from "../components/admin/AttachmentsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` },
});

function fmtMoney(v) {
  const n = Number(v || 0);
  return `$${n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  } catch { return d; }
}

const EMPTY_DRAFT = {
  date: new Date().toISOString().slice(0, 10),
  category: "",
  description: "",
  amount: "",
  notes: "",
  booking_id: "",
};

export default function AdminCosts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  // Filters
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fCategory, setFCategory] = useState("all");
  const [fBookingId, setFBookingId] = useState("");
  const [fQuery, setFQuery] = useState("");

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, else cost row
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/costs/categories`, auth());
      setCategories(r.data?.categories || []);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    }
  }, [navigate]);

  const loadCosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fFrom) params.set("from", fFrom);
      if (fTo) params.set("to", fTo);
      if (fCategory && fCategory !== "all") params.set("category", fCategory);
      if (fBookingId.trim()) params.set("booking_id", fBookingId.trim());
      if (fQuery.trim()) params.set("q", fQuery.trim());
      params.set("page_size", "200");
      const r = await axios.get(`${API}/admin/costs?${params.toString()}`, auth());
      setRows(r.data?.items || []);
      setTotalCount(r.data?.total_count || 0);
      setTotalAmount(r.data?.total_amount || 0);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
      else toast.error(e.response?.data?.detail || "Failed to load costs");
    } finally {
      setLoading(false);
    }
  }, [fFrom, fTo, fCategory, fBookingId, fQuery, navigate]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadCosts(); }, [loadCosts]);

  const categoryColours = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.key] = c.colour; });
    return m;
  }, [categories]);

  const openCreate = () => {
    setEditing(null);
    setDraft({
      ...EMPTY_DRAFT,
      category: categories[0]?.key || "",
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setDraft({
      date: row.date,
      category: row.category,
      description: row.description,
      amount: String(row.amount),
      notes: row.notes || "",
      booking_id: row.booking_id || "",
    });
    setModalOpen(true);
  };

  const submit = async () => {
    if (!draft.date) { toast.error("Date is required"); return; }
    if (!draft.category) { toast.error("Category is required"); return; }
    if (!draft.description.trim()) { toast.error("Description is required"); return; }
    const amt = Number(draft.amount);
    if (!amt || amt <= 0) { toast.error("Amount must be greater than 0"); return; }
    if (amt > 99999.99) { toast.error("Amount must be ≤ $99,999.99"); return; }

    setSaving(true);
    try {
      const body = {
        date: draft.date,
        category: draft.category,
        description: draft.description.trim(),
        amount: Math.round(amt * 100) / 100,
        notes: draft.notes,
        booking_id: draft.booking_id.trim() || null,
      };
      if (editing) {
        await axios.patch(`${API}/admin/costs/${editing.id}`, body, auth());
        toast.success("Cost updated");
      } else {
        await axios.post(`${API}/admin/costs`, body, auth());
        toast.success("Cost added");
      }
      setModalOpen(false);
      loadCosts();
    } catch (e) {
      const detail = e.response?.data?.detail || "Save failed";
      toast.error(String(detail).replace(/_/g, " "));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/costs/${deleteTarget.id}`, auth());
      toast.success("Cost deleted");
      setDeleteTarget(null);
      loadCosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const resetFilters = () => {
    setFFrom(""); setFTo(""); setFCategory("all"); setFBookingId(""); setFQuery("");
  };

  return (
    <AdminLayout title="Costs" subtitle="Track business expenses · Feeds into Net Profit on the Analytics → Profitability tab and the Dashboard Net Profit card.">
      {/* Header / actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 text-[#676664]">
          <Receipt className="w-5 h-5 text-[#9A7B4F]" strokeWidth={1.5} />
          <span className="text-sm">{totalCount} entries · Total <span className="text-[#282828] font-medium">{fmtMoney(totalAmount)}</span></span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate("/admin/analytics?tab=profitability")}
            variant="outline"
            className="border-[#9A7B4F]/40 text-[#9A7B4F] hover:bg-[#9A7B4F]/5"
            data-testid="costs-view-profit-btn"
          >
            <TrendingUp className="w-4 h-4 mr-2" strokeWidth={1.5} />View Costs &amp; Profitability report
          </Button>
          <Button
            onClick={openCreate}
            className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white"
            data-testid="costs-add-btn"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Add Cost
          </Button>
        </div>
      </div>

      {/* Filters bar */}
      <div className="bg-white p-4 border border-[#282828]/10 mb-6">
        <div className="flex items-center gap-2 mb-3 text-[#676664]/70 text-xs uppercase tracking-[0.08em]">
          <Filter className="w-3.5 h-3.5" strokeWidth={1.5} />Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-[10px] text-[#676664]/60 uppercase tracking-wider">From</Label>
            <Input
              type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)}
              className="mt-1 border-[#282828]/10"
              data-testid="costs-filter-from"
            />
          </div>
          <div>
            <Label className="text-[10px] text-[#676664]/60 uppercase tracking-wider">To</Label>
            <Input
              type="date" value={fTo} onChange={(e) => setFTo(e.target.value)}
              className="mt-1 border-[#282828]/10"
              data-testid="costs-filter-to"
            />
          </div>
          <div>
            <Label className="text-[10px] text-[#676664]/60 uppercase tracking-wider">Category</Label>
            <Select value={fCategory} onValueChange={setFCategory}>
              <SelectTrigger className="mt-1 border-[#282828]/10" data-testid="costs-filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] text-[#676664]/60 uppercase tracking-wider">Booking ID</Label>
            <Input
              value={fBookingId} onChange={(e) => setFBookingId(e.target.value)}
              placeholder="e.g. MHR-260528-…"
              className="mt-1 border-[#282828]/10"
              data-testid="costs-filter-booking"
            />
          </div>
          <div>
            <Label className="text-[10px] text-[#676664]/60 uppercase tracking-wider">Search</Label>
            <div className="relative mt-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#676664]/40" strokeWidth={1.5} />
              <Input
                value={fQuery} onChange={(e) => setFQuery(e.target.value)}
                placeholder="Description / notes"
                className="pl-8 border-[#282828]/10"
                data-testid="costs-filter-q"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={resetFilters} className="text-[11px] text-[#676664]/60 hover:text-[#9A7B4F]" data-testid="costs-filter-reset">Reset filters</button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#282828]/10 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" strokeWidth={1.5} />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-20 text-center" data-testid="costs-empty">
            <Receipt className="w-10 h-10 mx-auto text-[#676664]/20 mb-3" strokeWidth={1.2} />
            <p className="text-[#676664]/60 text-sm">No costs found</p>
            <p className="text-[#676664]/40 text-xs mt-1">Costs will appear here</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAF8F0] text-[10px] uppercase tracking-[0.08em] text-[#676664]/70">
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Description</th>
                <th className="text-left px-4 py-3">Booking</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-right px-4 py-3 w-24">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="costs-table">
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#282828]/5 hover:bg-[#FAF8F0]/40 transition-colors" data-testid={`cost-row-${r.id}`}>
                  <td className="px-4 py-3 text-[#676664] whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1"
                      style={{ backgroundColor: `${categoryColours[r.category] || "#9A7B4F"}15`, color: categoryColours[r.category] || "#9A7B4F" }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: categoryColours[r.category] || "#9A7B4F" }} />
                      {r.category_label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#282828]">
                    <div className="font-medium">{r.description}</div>
                    {r.notes && <div className="text-[11px] text-[#676664]/50 mt-0.5 truncate max-w-md">{r.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-[#676664]/70 whitespace-nowrap font-mono">
                    {r.booking_id || <span className="text-[#676664]/30">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-[#282828] font-medium whitespace-nowrap">
                    {fmtMoney(r.amount)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(r)}
                      className="text-[#676664]/60 hover:text-[#9A7B4F] p-1 mr-1"
                      data-testid={`cost-edit-${r.id}`}
                      title="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(r)}
                      className="text-[#676664]/60 hover:text-[#B23A48] p-1"
                      data-testid={`cost-delete-${r.id}`}
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-[#282828]/10 bg-[#FAF8F0]/50">
                <td colSpan={4} className="px-4 py-3 text-right text-[11px] text-[#676664]/60 uppercase tracking-wider">Total this view</td>
                <td className="px-4 py-3 text-right text-[#282828] font-medium" data-testid="costs-total-amount">{fmtMoney(totalAmount)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* Create / Edit modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md" data-testid="cost-modal">
          <DialogHeader>
            <DialogTitle className="text-xl text-[#676664]">{editing ? "Edit Cost" : "Add Cost"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-[11px] text-[#676664]/70">Date *</Label>
              <Input
                type="date" value={draft.date}
                onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                className="mt-1 border-[#282828]/10"
                data-testid="cost-modal-date"
              />
            </div>
            <div>
              <Label className="text-[11px] text-[#676664]/70">Category *</Label>
              <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                <SelectTrigger className="mt-1 border-[#282828]/10" data-testid="cost-modal-category">
                  <SelectValue placeholder="Choose…" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {draft.category === "outfit_purchase" && (
                <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 px-2 py-1.5 mt-2 leading-snug"
                   data-testid="outfit-purchase-warning">
                  <b>Enter outfit purchase costs in Catalogue Edit</b> to link them to a product.
                  Adding them here as well will double-count against product profitability.
                </p>
              )}
            </div>
            <div>
              <Label className="text-[11px] text-[#676664]/70">Description *</Label>
              <Input
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                maxLength={200}
                placeholder="e.g. Roshan post-Kim dry clean"
                className="mt-1 border-[#282828]/10"
                data-testid="cost-modal-description"
              />
            </div>
            <div>
              <Label className="text-[11px] text-[#676664]/70">Amount (AUD, gross of GST) *</Label>
              <Input
                type="number" step="0.01" min="0" max="99999.99"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                placeholder="0.00"
                className="mt-1 border-[#282828]/10"
                data-testid="cost-modal-amount"
              />
            </div>
            <div>
              <Label className="text-[11px] text-[#676664]/70">Booking ID (optional)</Label>
              <Input
                value={draft.booking_id}
                onChange={(e) => setDraft({ ...draft, booking_id: e.target.value })}
                placeholder="e.g. MHR-260528-8W4M"
                className="mt-1 border-[#282828]/10 font-mono text-[12px]"
                data-testid="cost-modal-booking"
              />
              <p className="text-[10px] text-[#676664]/40 mt-1">Outfit will be auto-linked from the booking.</p>
            </div>
            <div>
              <Label className="text-[11px] text-[#676664]/70">Notes</Label>
              <Textarea
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                maxLength={2000}
                rows={3}
                className="mt-1 border-[#282828]/10"
                data-testid="cost-modal-notes"
              />
            </div>
            {editing ? (
              <AttachmentsPanel parentType="business_cost" parentId={editing.id} compact />
            ) : (
              <div className="text-[11px] text-[#676664]/50 italic border border-dashed border-[#282828]/10 px-3 py-2">
                Save the cost first to attach files.
              </div>
            )}
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setModalOpen(false)} className="border-[#282828]/10" data-testid="cost-modal-cancel">Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white"
              data-testid="cost-modal-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing ? "Save changes" : "Add Cost"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="cost-delete-modal">
          <DialogHeader>
            <DialogTitle className="text-lg text-[#676664] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#B23A48]" strokeWidth={1.5} />Delete cost?
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="text-sm text-[#676664] space-y-2 my-3">
              <p><span className="text-[#676664]/50">Category:</span> {deleteTarget.category_label}</p>
              <p><span className="text-[#676664]/50">Description:</span> {deleteTarget.description}</p>
              <p><span className="text-[#676664]/50">Amount:</span> <span className="text-[#282828] font-medium">{fmtMoney(deleteTarget.amount)}</span></p>
              <p className="text-[11px] text-[#676664]/40 pt-2">This action is permanent.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="border-[#282828]/10" data-testid="cost-delete-cancel">Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-[#B23A48] hover:bg-[#9A2D3A] text-white"
              data-testid="cost-delete-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
